import {
  Form,
  Modal,
  Button,
  HoursInput,
  SelectInput,
  TextAreaInput,
  CheckboxInput,
  ButtonsWrapper,
  DatePickerInput,
} from '@thinknet-gmbh/platform-common-ui';

import { useEffect } from 'react';
import isBoolean from 'lodash/isBoolean';
import { useTranslation } from 'react-i18next';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetAbsenceReasonsByCompany } from 'src/hooks/absence-reasons/get-absence-reasons-by-company.hook';

import { IAbsenceGanttFormModal } from './absence-gantt-form-modal.interfaces';

const AbsenceGanttFormModal = ({
  onClose,
  absence,
  onSubmitAbsenceModal,
}: IAbsenceGanttFormModal) => {
  const { t, i18n } = useTranslation(['employee', 'common']);
  const [form] = Form.useForm();

  const currentCompanyId = useGetCurrentCompanyId();
  const { absenceReasons } = useGetAbsenceReasonsByCompany(currentCompanyId);

  useEffect(() => form.setFieldsValue(absence), [form, absence]);

  return (
    <Modal
      isOpened
      onClose={onClose}
      title={t(`employee:labels.${absence ? 'edit' : 'create'}Absence`)}
    >
      <Form layout="vertical" form={form} onFinish={onSubmitAbsenceModal}>
        <Form.Item
          name="absenceReasonId"
          label={t('employee:labels.absenceType')}
          rules={[{ required: true, message: t('employee:validationError.absenceTypeRequired') }]}
        >
          <SelectInput placeholder={t('employee:labels.selectAbsenceType')}>
            {absenceReasons.map((item: any) => (
              <SelectInput.Option value={item._id} key={item._id}>
                {item.name}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name="date"
          label={t('employee:labels.date')}
          rules={[{ required: true, message: t('employee:validationError.dateRequired') }]}
        >
          <DatePickerInput.RangePicker lang={i18n.language} />
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const date = getFieldValue('date');
            const fullDay = getFieldValue('fullDay');

            if (date || isBoolean(fullDay)) {
              const [from, to] = date;
              const isOneDayRange = from.toISOString() === to.toISOString();

              return (
                <>
                  {isOneDayRange && (
                    <Form.Item
                      name="fullDay"
                      valuePropName="checked"
                      initialValue
                      style={{ marginTop: '-8px' }}
                    >
                      <CheckboxInput>{t('employee:labels.fullDay')}</CheckboxInput>
                    </Form.Item>
                  )}
                  {isOneDayRange && !fullDay && (
                    <Form.Item name="hours" label={t('employee:labels.hours')}>
                      <HoursInput placeholder="4 hr" />
                    </Form.Item>
                  )}
                </>
              );
            }
          }}
        </Form.Item>

        <Form.Item name="details" label={t('employee:labels.details')}>
          <TextAreaInput
            showCount
            maxLength={200}
            placeholder={t('employee:labels.writeDetails')}
          />
        </Form.Item>
        <ButtonsWrapper marginTop={16}>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};

export default AbsenceGanttFormModal;
